import React, { useRef, useLayoutEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import FilterDropdown from "../../../components/ui-components/FilterDropdown";
import BudgetScopeSummary from "../../../components/Budgets/BudgetScopeSummary";
import SkeletonScopeCard from "../../../components/Budgets/SkeletonScopeCard";
import ScopeErrorState from "../../../components/Budgets/ScopeErrorState";
import EmptyScopeState from "../../../components/Budgets/EmptyScopeState";
import { Panel, PanelSection, PANEL_EYEBROW } from "./Panel";
import { controlPoints } from "../utils/policyControls";

// BudgetDefinitionPanel — one "Budget details" panel, not four cards.
//
// Name, ownership, scope and control policy are all the same kind of thing: the standing
// facts about this budget. Giving each its own bordered card and its own uppercase heading
// made four competing objects out of one, and made the left rail read as loud as the
// workspace beside it. They are now sections of a single surface, separated by dividers.
//
// The helper copy is deliberately thin here. Explaining what a control point does matters
// while you are choosing one; during review it is noise, and the policy page is one click
// away for the full ladder.

const BudgetDefinitionPanel = ({
  details,
  onDetailsChange,
  owners,
  onOwnersChange,
  userOptions = [],
  scope,
  dimensions,
  onOpenScopeSheet,
  scopeQueryStatus,
  scopeRefetch,
  autoFocusName = false,
  enforcementPolicies,
}) => {
  const { showSkeleton, isRefreshing, hasScope, isError } =
    scopeQueryStatus || {};

  const nameInputRef = useRef(null);
  useLayoutEffect(() => {
    if (!autoFocusName) return;
    const id = setTimeout(() => {
      nameInputRef.current?.focus({ preventScroll: false });
    }, 150);
    return () => clearTimeout(id);
  }, [autoFocusName]);

  const financeSelected =
    userOptions.find((o) => o.value === (owners?.finance_owners || [])[0]) ||
    null;

  const opsSelected =
    userOptions.find((o) => o.value === (owners?.ops_owners || [])[0]) || null;

  // "" is the org-default sentinel the parent already round-trips, so it stays the
  // first option rather than becoming a separate placeholder state.
  const policyOptions = useMemo(
    () => [
      { value: "", label: "Organization default" },
      ...(enforcementPolicies || []).map((p) => ({
        value: String(p.id),
        label: p.name,
      })),
    ],
    [enforcementPolicies]
  );

  const selectedPolicy = useMemo(
    () =>
      (enforcementPolicies || []).find(
        (p) => String(p.id) === String(details?.policy_id ?? "")
      ) || null,
    [enforcementPolicies, details?.policy_id]
  );
  const points = useMemo(() => controlPoints(selectedPolicy), [selectedPolicy]);

  return (
    <Panel className="min-w-0 w-full">
      <PanelSection title="Budget details" className="space-y-4">
        <div className="w-full min-w-0" style={{ width: "100%" }}>
          <label htmlFor="budget-name" className={PANEL_EYEBROW}>
            Name
          </label>
          <input
            id="budget-name"
            ref={nameInputRef}
            type="text"
            className="mt-1 block box-border p-2 border border-border rounded-ctrl text-sm"
            style={{ width: "100%", minWidth: 0, boxSizing: "border-box" }}
            value={details?.name || ""}
            onChange={(e) =>
              onDetailsChange({ ...details, name: e.target.value })
            }
            placeholder="e.g. Q1 Engineering Budget"
            autoFocus={autoFocusName}
          />
        </div>

        <div>
          <p className={PANEL_EYEBROW}>Owner</p>
          <div className="mt-1">
            <FilterDropdown
              ariaLabel="Finance owner"
              options={userOptions}
              value={financeSelected?.value}
              onChange={(v) =>
                onOwnersChange({
                  ...owners,
                  finance_owners: v != null && v !== "" ? [v] : [],
                })
              }
              fullWidth
            />
          </div>
        </div>

        <div>
          <p className={PANEL_EYEBROW}>Operations owner</p>
          <div className="mt-1">
            <FilterDropdown
              ariaLabel="Ops owner"
              options={userOptions}
              value={opsSelected?.value}
              onChange={(v) =>
                onOwnersChange({
                  ...owners,
                  ops_owners: v != null && v !== "" ? [v] : [],
                })
              }
              fullWidth
            />
          </div>
        </div>
      </PanelSection>

      {/* Scope — 4-state: skeleton | error | summary | empty. Each state renders `bare`,
          because this section already supplies the surface and the heading. */}
      <PanelSection
        title="Scope"
        action={
          onOpenScopeSheet && hasScope && !showSkeleton && !isError ? (
            <button
              type="button"
              onClick={onOpenScopeSheet}
              className="text-sm font-medium text-primary hover:text-primary-hover transition-colors cursor-pointer"
            >
              Edit
            </button>
          ) : null
        }
      >
        {showSkeleton ? (
          <SkeletonScopeCard bare />
        ) : isError ? (
          <ScopeErrorState onRetry={scopeRefetch} bare />
        ) : hasScope ? (
          <BudgetScopeSummary
            scope={scope}
            dimensions={dimensions}
            isRefreshing={isRefreshing}
            bare
          />
        ) : (
          <EmptyScopeState
            onOpenBuilder={onOpenScopeSheet}
            isRefreshing={isRefreshing}
            bare
          />
        )}
      </PanelSection>

      {/* Control policy — the policy, not a "budget type".
          Soft and hard are not two kinds of budget: one authorization can carry both, as
          steps in the policy that governs it (warn at 80%, review a projected breach,
          approval above the amount). The budget definition stays clean — name, ownership,
          scope, amount — and the control behaviour is named here, with the full ladder one
          click away. */}
      <PanelSection
        title="Control policy"
        action={
          <Link
            to="/budget-enforcement/policies"
            className="text-sm font-medium text-primary hover:underline"
          >
            View policy
          </Link>
        }
      >
        <FilterDropdown
          ariaLabel="Control policy"
          options={policyOptions}
          value={details.policy_id || ""}
          onChange={(v) => onDetailsChange({ ...details, policy_id: v })}
          fullWidth
          height={38}
        />

        {points.length > 0 && (
          <ul className="mt-3 flex flex-col gap-1">
            {points.map((point) => (
              <li
                key={point}
                className="flex items-start gap-2 text-xs text-text-2"
              >
                <span
                  aria-hidden
                  className="mt-1.5 inline-block rounded-full bg-text-3"
                  style={{ width: 4, height: 4 }}
                />
                {point}
              </li>
            ))}
          </ul>
        )}
      </PanelSection>
    </Panel>
  );
};

export default BudgetDefinitionPanel;
